import { useMemo, memo } from 'react';

import { useCheckoutContext, Article, PriceType } from '@zooplus/checkout-state';
import { ProductPrice } from '@zooplus/ui-components';

import {
  CHECKOUT_STATE_PROJECT_ID,
  PAGE_TYPE,
  ARTICLE_PRICE_TYPE,
  ARTICLE_PRICE_TYPE_V2,
} from '@utilities/constants';

type ItemPriceCartV1Props = {
  article: Article;
  commonStyle?: string;
};

const PRICE_TYPE_MAPPING = {
  [ARTICLE_PRICE_TYPE.SUPPLIER_REDUCED]: ARTICLE_PRICE_TYPE_V2.RETAIL,
  [ARTICLE_PRICE_TYPE.STANDARD_REDUCED]: ARTICLE_PRICE_TYPE_V2.OFFER,
  [ARTICLE_PRICE_TYPE.SET_ARTICLE]: ARTICLE_PRICE_TYPE_V2.SET,
  [ARTICLE_PRICE_TYPE.STANDARD]: ARTICLE_PRICE_TYPE_V2.REGULAR,
} as const;

const getPrice = (article: Article): string => article.formattedActualPrice ?? '';

const getPricePrefix = (article: Article): string | undefined => {
  if (article.beforePriceLabel && article.formattedBeforePrice) {
    return `${article.beforePriceLabel} ${article.formattedBeforePrice}`;
  }
  return undefined;
};

const getRefPrice = (article: Article, isOmnibusDirectiveFeatureEnabled: boolean) => ({
  amount: article.formattedBeforePrice as string,
  label: article.beforePriceLabel as string,
  difference:
    isOmnibusDirectiveFeatureEnabled && article.percentageDiscount
      ? `${article.percentageDiscount}%`
      : '',
});

const getTooltipProps = (article: Article, isOmnibusDirectiveFeatureEnabled: boolean) => {
  if (isOmnibusDirectiveFeatureEnabled && article.localizedTooltipText) {
    return {
      id: 'product-reduced-price-tooltip',
      label: 'more info',
      text: article.localizedTooltipText,
      width: 'md' as const,
      position: 'right-middle' as const,
      breakpointPlacements: {
        lg: { position: 'top-middle' as const },
      },
    };
  }
  return undefined;
};

const getTextAlign = (isPreviewPage: boolean) => ({
  all: 'left' as 'right' | 'left',
  md: (isPreviewPage ? 'right' : 'left') as 'right' | 'left',
});

const usePriceProps = (
  article: Article,
  isPreviewPage: boolean,
  isOmnibusDirectiveFeatureEnabled: boolean,
) => {
  return useMemo(
    () => ({
      tooltipProps: getTooltipProps(article, isOmnibusDirectiveFeatureEnabled),
      price: getPrice(article),
      refPrice: getRefPrice(article, isOmnibusDirectiveFeatureEnabled),
      pricePrefix: getPricePrefix(article),
      textAlign: getTextAlign(isPreviewPage),
    }),
    [article, isOmnibusDirectiveFeatureEnabled, isPreviewPage],
  );
};

const ItemPriceCartV1 = ({ article, commonStyle }: ItemPriceCartV1Props) => {
  const { mainTogglesMap = {}, pageType } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const isOmnibusDirectiveFeatureEnabled = mainTogglesMap.CHECKOUT_OMNIBUS_DIRECTIVE_FEATURE;
  const isCartPage = pageType === PAGE_TYPE.WEB_CART;
  const isPreviewPage = pageType === PAGE_TYPE.PREVIEW;
  const isReducedPrice = article.priceType !== ARTICLE_PRICE_TYPE.STANDARD;

  const productPriceType = PRICE_TYPE_MAPPING[article.priceType] ?? ARTICLE_PRICE_TYPE_V2.REGULAR;

  const priceProps = usePriceProps(article, isPreviewPage, isOmnibusDirectiveFeatureEnabled);

  return (
    <ProductPrice
      data-testid="article-price"
      isReducedPrice={isReducedPrice}
      className={commonStyle}
      isPriceBold={isCartPage}
      textAlign={priceProps.textAlign}
      price={priceProps.price}
      priceType={productPriceType as PriceType}
      pricePrefix={priceProps.pricePrefix}
      refPrice={priceProps.refPrice}
      tooltip={priceProps.tooltipProps}
    />
  );
};

export default memo(ItemPriceCartV1);
